/**
 * Light / Dark / System theme preference (docs/brand.md → Themes).
 *
 * System is the absence of a choice: no `data-theme` on `<html>`, so daisyUI's
 * `prefersdark` lets the OS decide. An explicit choice pins `data-theme` and makes both
 * per-scheme `theme-color` metas report the chosen theme's colour.
 *
 * Kept import-free: vite.config.ts loads this file to inline `themeInitScript` into
 * every page's `<head>`, and the config loader can't resolve `@mockd/shared`.
 */

export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

export const THEME_STORAGE_KEY = 'mockd_theme';

/** `THEME_COLOR` from the brand tokens, passed in to keep this file import-free. */
export interface ThemeColors {
  dark: string;
  light: string;
}

/** Anything but an explicit `light` or `dark`, including nothing stored, is System. */
export function parseThemePreference(value: string | null): ThemePreference {
  return value === 'light' || value === 'dark' ? value : 'system';
}

const schemeOf = (meta: HTMLMetaElement): keyof ThemeColors =>
  (meta.getAttribute('media') ?? '').includes('dark') ? 'dark' : 'light';

/** Apply a preference to the live document. `themeInitScript` must do exactly the same. */
export function applyTheme(preference: ThemePreference, colors: ThemeColors): void {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', `mockd-${preference}`);

  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach(meta => {
    meta.setAttribute('content', colors[preference === 'system' ? schemeOf(meta) : preference]);
  });
}

/**
 * `applyTheme` for the stored preference, as an inline script for the end of `<head>`.
 * Running before `<body>` is parsed is what stops the prerendered pages flashing the OS
 * theme before React loads. It has to follow the theme-color metas it rewrites, and is
 * ES5 with a try/catch because localStorage can throw (privacy modes, blocked storage).
 */
export function themeInitScript(colors: ThemeColors): string {
  return (
    '(function(){try{' +
    `var p=localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)}),c=${JSON.stringify(colors)},r=document.documentElement;` +
    "if(p!=='light'&&p!=='dark')p=null;" +
    "if(p)r.setAttribute('data-theme','mockd-'+p);else r.removeAttribute('data-theme');" +
    "var m=document.querySelectorAll('meta[name=\"theme-color\"]');" +
    "for(var i=0;i<m.length;i++)m[i].setAttribute('content',c[p||((m[i].getAttribute('media')||'').indexOf('dark')>-1?'dark':'light')]);" +
    '}catch(e){}})()'
  );
}
